import { Suspense, useMemo } from 'react'
import { Virtuoso } from 'react-virtuoso'

import { paths } from 'src/config/paths'
import { SCROLL_VELOCITY_THRESHOLD } from 'src/lib/constants'
import { sendEvent } from 'src/shared/api/events/send-event'
import { useUsersBySearch } from 'src/shared/features/users/api/accounts/use-users-by-search'
import UserSearchCard, { UserSearchCardSkeleton } from 'src/shared/features/users/components/cards/user-search-card'
import type { PublicUserData } from 'src/shared/features/users/schemas/user'
import { useEventStore } from 'src/shared/store/event-store'

const SearchResultsSkeleton = () => {
  return (
    <div className="flex flex-col gap-2">
      <UserSearchCardSkeleton />
      <UserSearchCardSkeleton />
      <UserSearchCardSkeleton />
    </div>
  )
}

const Footer = ({
  query,
  isFetchingNextPage,
  hasUsers,
}: {
  query: string
  isFetchingNextPage: boolean
  hasUsers: boolean
}) => {
  if (isFetchingNextPage) return <SearchResultsSkeleton />

  if (!hasUsers) {
    return (
      <div className="my-2 pb-5 text-sm font-medium text-muted">
        No results for <span className="font-bold">{query}</span>
      </div>
    )
  }

  return (
    <div className="my-2 pb-5 text-sm font-medium text-muted">
      No more results for <span className="font-bold">{query}</span>
    </div>
  )
}

const SearchResults = ({ query }: { query: string }) => {
  const { data: searchResults, hasNextPage, fetchNextPage, isFetchingNextPage } = useUsersBySearch(6, query)

  const users = useMemo(() => {
    const allUsers = searchResults?.pages.flatMap((page) => page.results) || []
    return [...new Map(allUsers.map((user) => [user.id, user])).values()]
  }, [searchResults])

  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const setFlowId = eventStore.setFlowId
  const setSessionId = eventStore.setSessionId

  const handleCardClick = (user: PublicUserData) => {
    // Default navigation behavior
    if (!sessionId) {
      setSessionId()
    }
    const newFlowId = setFlowId()
    sendEvent({
      userId: user.id,
      eventType: 'flow_start',
      info: {
        sessionId,
        flow_type: 'send_referral',
        flow_step: 'select_searched_user',
        flow_id: newFlowId,
      },
    })
    window.open(paths.users.profile.getHref(user.username), '_blank')
  }

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  return (
    <Virtuoso
      data={users}
      endReached={loadMore}
      overscan={200}
      itemContent={(index, user: PublicUserData) => (
        <UserSearchCard key={user.id} user={user} onClick={() => handleCardClick(user)} />
      )}
      components={{
        Footer: () => <Footer query={query} isFetchingNextPage={isFetchingNextPage} hasUsers={users.length > 0} />,
        ScrollSeekPlaceholder: SearchResultsSkeleton,
      }}
      scrollSeekConfiguration={{
        enter: (velocity) => Math.abs(velocity) > SCROLL_VELOCITY_THRESHOLD,
        exit: (velocity) => Math.abs(velocity) < 10,
      }}
      style={{
        height: '100%',
        scrollbarWidth: 'none',
        overflowY: 'auto',
        overflowX: 'hidden',
        msOverflowStyle: 'none',
      }}
      className="[&::-webkit-scrollbar]:hidden"
    />
  )
}

const SearchContent = ({ query }: { query: string }) => {
  return (
    <Suspense fallback={<SearchResultsSkeleton />}>
      <SearchResults query={query} />
    </Suspense>
  )
}

export default SearchContent
